import { IThunkArguments } from '../reduxTypes';
import { SIGNING_IN_USER, FIRST_TIME_SIGNIN } from './index';
import { resolveEnvironment } from '../../EnvironmentResolver';
import { EMAIL_NOT_VERIFIED, EMAIL_VERIFIED, VERIFY_EMAIL, GET_USER, CREATE_USER, FLUSH_USER, USER_ERROR, INIT_SIGNIN } from '../reducers/userManagementReducer/userManagementReducer';
import { EmailEnvironment } from '../../pages/EmailConfirmation/EmailConfirmation';
import { IDeveloperDetailsRequest } from '../../API/ApiManagementTypes';

/**
 * This file is responsible for all redux dispatches with regards to authentication
 * Currently supports login, logout, and silent login with and without fallbacks
 */

/**
 * This is a redirect url - this comment is only made so the IDE doesn't document it poorly
 */
const {
    SILENT_REDIRECT_URL: silentRedirectUrl,
    REDIRECT_URL: redirectUrl
} = resolveEnvironment()

export const GitHubAuth = 'urn:se:curity:authentication:github:Github';
export const AzureAuth = 'urn:se:curity:authentication:oidc:Azure';

function parseJwt(token: any) {
    if (!token) throw new Error('parseJwt: token is missing');
    const base64Url = token.split('.')[1];
    if (!base64Url) throw new Error('parseJwt: token is malformed');
    const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + (c.codePointAt(0) ?? 0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

export const params = {
    scope: [
        "webportalapi.read",
        "webportalapi.readwrite",
        "openid",
        "profile",
        "email"
    ],
    response_type: ["token", "id_token"]
}

export const mobileParams = {
    scope: [
        "webportalapi.read",
        "webportalapi.readwrite",
        "openid",
        "profile",
        "email"
    ],
    response_type: ["token", "id_token"]
}

/* istanbul ignore next */
export function logIn(acr_value: string | null = null) {
    return (dispatch: any, getState: any, { silentAssistant }: IThunkArguments) => {
        let customParams = null;
        if (acr_value) {
            customParams = { ...params, acr_values: acr_value }
        }
        dispatch({
            type: FIRST_TIME_SIGNIN,
            payload: silentAssistant.authorizePopup(customParams ?? params).then(() => {
                const data = parseJwt(silentAssistant.getIDToken())
                return {
                    username: data.name,
                    email: data.email,
                    token: silentAssistant.getToken()
                }
            })
        }).catch((error: any) => console.log('Here', error)) //Log the error
    }
}

/**
 * The main function for authenticating users into the developer portal
 * @param acr_value  optional argument if a specific authenticator is wanted (set to github on prod)
 * @returns 
 */
/* istanbul ignore next */
export function logInMobile(acr_value: string | null = null) {
    return (dispatch: any, getState: any, { assistant }: IThunkArguments) => {
        let customParams: any = {
            ...params,
            redirectUrl: redirectUrl
        };
        if (acr_value) {
            customParams = { ...customParams, acr_values: acr_value }
        }
        console.log(customParams)
        dispatch({
            type: FIRST_TIME_SIGNIN,
            payload: assistant.authorize((customParams ?? params)).then(() => {
                const data = parseJwt(assistant.getIDToken())
                return {
                    username: data.name,
                    email: data.email,
                    token: assistant.getToken()
                }
            })
        }).catch((error: any) => console.log('Here', error)) //Log the error
    }
}

/* istanbul ignore next */
export function logOut() {
    return (dispatch: any, getState: any, { assistant }: IThunkArguments) => {
        assistant.revoke().catch((_error: any) => "");
        assistant.logout(silentRedirectUrl, true);

        dispatch({
            type: 'SIGNED_OUT_USER'
        })
    }
}

/**
 * Action creator dispatches a hidden login if access token has expired
 * @returns Promise dispatch to tokenAssistantReducer
 */
/* istanbul ignore next */
export function logInIfRequired() {
    return (dispatch: any, getState: any, { silentAssistant, assistant }: IThunkArguments) => {

        const authenticated = silentAssistant?.getToken() && silentAssistant?.getExpiresIn();
        if (!authenticated) {
            dispatch({
                type: INIT_SIGNIN,
                payload: silentAssistant.init().then(() => {
                    dispatch({
                        type: SIGNING_IN_USER,
                        payload: silentAssistant?.authorizeHiddenFallbackToPopup(params).then(() => {
                            const data = parseJwt(silentAssistant.getIDToken())
                            return {
                                username: data.name,
                                email: data.email,
                                acr: data.acr,
                                token: silentAssistant.getToken()
                            }
                        }).catch(() => {
                            throw new Error('ehh')
                        })
                    }).catch((error: any) => console.log('Here', error)) //Log the error
                }).catch((error: any) => console.log('Here', error))
            }).catch((error: any) => console.log('Here', error))
        }

    }
}

/* istanbul ignore next */
export function checkAuthorization() {
    return (dispatch: any, getState: any, { silentAssistant }: IThunkArguments) => {
        dispatch({
            type: SIGNING_IN_USER,
            payload: silentAssistant.authorizeHiddenFrame(params).then(
                () => {
                    const data = parseJwt(silentAssistant.getIDToken())

                    return {
                        username: data.name,
                        token: silentAssistant.getToken()
                    }
                }
            )
        }).catch((error: any) => console.log('Here', error)) //Log the error

    }
}

/**
 * 
 * 
 */
/* istanbul ignore next */
export function turnOffRedirect() {
    return (dispatch: any, getState: any, { silentAssistant }: IThunkArguments) => {
        dispatch({
            type: 'PREVENT_REDIRECT'
        })
    }
}

/**
 * Function resends email verification to TPP
 * @param id id of TPP to confirm
 * @param prod is this a sandbox or production TPP? Default sandbox (false)
 */
export function sendEmailVerification(id: string, environment: EmailEnvironment = EmailEnvironment.Sandbox) {
    return (dispatch: any, getState: any, { silentAssistant, sandboxAxiosInstance, productionAxiosInstance }: IThunkArguments) => {
        const token = silentAssistant.getToken()
        if (environment === EmailEnvironment.Production) {
            dispatch({
                type: 'PLACEHOLDER', //Might not need to do anything with this
                payload: productionAxiosInstance.post(
                    `/accountmanagement/tpps/${id}/emailverification/resend`, {},
                    { headers: { 'Authorization': `Bearer ${token}` } }
                ).then((response) => {
                    return response.data
                })
            }).catch((error: any) => console.log(error))
        } else if (environment === EmailEnvironment.Sandbox) {
            dispatch({
                type: 'PLACEHOLDER', //Might not need to do anything with this
                payload: sandboxAxiosInstance.post(
                    `/accountmanagement/tpps/${id}/emailverification/resend`, {},
                    { headers: { 'Authorization': `Bearer ${token}` } }
                ).then((response) => {
                    return response.data
                })
            }).catch((error: any) => console.log(error))
        } else {
            dispatch({
                type: 'PLACEHOLDER', //Might not need to do anything with this
                payload: sandboxAxiosInstance.post(
                    `/usermanagement/users/emailverification/resend`, {},
                    { headers: { 'Authorization': `Bearer ${token}` } }
                ).then((response) => {
                    return response.data
                })
            }).catch((error: any) => console.log(error))
        }
    }
}

/**
 * Function confirms email address of TPP
 * @param ticketID ticket TPP received in email
 * @param environment is this a sandbox or production TPP? Default sandbox (false)
 */
export function verifyEmail(ticketID: string, environment: EmailEnvironment = EmailEnvironment.Sandbox) {
    return (dispatch: any, getState: any, { silentAssistant, sandboxAxiosInstance: axiosInstance, productionAxiosInstance: prodInstance }: IThunkArguments) => {
        switch (environment) {
            case EmailEnvironment.Production:
                dispatch({
                    type: VERIFY_EMAIL, //Might not need to do anything with this
                    payload: prodInstance.post(
                        `/accountmanagement/tpps/emailverification/${ticketID}`, {},
                    ).then((response) => {
                        return response.data.data
                    })
                }).catch((error: any) => console.log(error))
                break;
            case EmailEnvironment.Sandbox:
                dispatch({
                    type: VERIFY_EMAIL, //Might not need to do anything with this
                    payload: axiosInstance.post(
                        `/accountmanagement/tpps/emailverification/${ticketID}`, {},
                    ).then((response) => {
                        return response.data.data
                    })
                }).catch((error: any) => console.log(error))
                break;
            case EmailEnvironment.User:
                console.log('We in the right place')
                dispatch({
                    type: VERIFY_EMAIL,
                    payload: axiosInstance.post(
                        `/usermanagement/users/emailverification/${ticketID}`, {},
                    ).then((response) => {
                        return response.data.data
                    })
                }).catch((error: any) => console.log(error))
        }
    }
}



export function setEmailVerified() {
    return (dispatch: any) => {
        dispatch({
            type: EMAIL_VERIFIED
        })
    }
}

export function setEmailNotVerified() {
    return (dispatch: any) => {
        dispatch({
            type: EMAIL_NOT_VERIFIED
        })
    }
}

export function getUser() {
    return (dispatch: any, getState: any, { assistant, sandboxAxiosInstance: axiosInstance }: IThunkArguments) => {
        const token = assistant.getToken();
        dispatch({
            type: GET_USER,
            payload: axiosInstance.get(
                `/usermanagement/users`,
                { headers: { 'Authorization': `Bearer ${token}` } }

            ).then((response) => response.data.data)
        }).catch((error: any) => console.log(error));
    }
}

export function createUser(body: IDeveloperDetailsRequest) {
    return (dispatch: any, getState: any, { assistant, sandboxAxiosInstance: axiosInstance }: IThunkArguments) => {
        const token = assistant.getToken();
        dispatch({
            type: CREATE_USER,
            payload: axiosInstance.post(
                `/usermanagement/users`,
                body,
                { headers: { 'Authorization': `Bearer ${token}` } }
            )
        }).catch((error: any) => {
            console.log({ errorM: error, response: error.response })
            dispatch({
                type: USER_ERROR,
                payload: error?.response?.data
            })
        })
    }
}

export function updateUser(body: IDeveloperDetailsRequest, id: string) {
    return (dispatch: any, getState: any, { assistant, sandboxAxiosInstance: axiosInstance }: IThunkArguments) => {
        const token = assistant.getToken();
        dispatch({
            type: CREATE_USER,
            payload: axiosInstance.put(
                `/usermanagement/users/${id}`,
                body,
                { headers: { 'Authorization': `Bearer ${token}` } }
            )
        }).catch((error: any) => {
            console.log({ errorM: error, response: error.response })
            dispatch({
                type: USER_ERROR,
                payload: error.response.data
            })
        })
    }
}

export function flushUser() {
    return (dispatch: any, getState: any, { assistant, sandboxAxiosInstance: axiosInstance }: IThunkArguments) => {
        dispatch({
            type: FLUSH_USER
        })
    }
}